import React, { useEffect, useState } from "react";
import { getGuides, Guide } from "../services/guidesService";

interface GuidesPageProps {
  onBack: () => void;
  purchasedItems?: string[];
  isLoggedIn?: boolean;
  onLogin?: () => void;
}

const GuidesPage: React.FC<GuidesPageProps> = ({
  onBack,
  purchasedItems = [],
  isLoggedIn = false,
  onLogin,
}) => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [guideContentMode, setGuideContentMode] = useState<
    "video" | "preview" | "pdf"
  >("preview");
  const [isContentLoading, setIsContentLoading] = useState(false);

  const purchasedGuia = (g?: Guide | null) => {
    if (!g) return false;
    if (g.purchased) return true;
    return purchasedItems.includes(g.id);
  };

  const resolveUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
    const base = bucket?.includes("firebasestorage")
      ? `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/`
      : "";
    const path = encodeURIComponent(url.replace(/^\//, ""));
    return base ? `${base}${path}?alt=media` : `/${decodeURIComponent(path)}`;
  };

  const buildVideoSrc = (url: string) => {
    if (!url) return "";
    if (url.includes("youtube.com/embed")) {
      return url.includes("?")
        ? `${url}&autoplay=1&mute=1`
        : `${url}?autoplay=1&mute=1`;
    }
    return url;
  };

  useEffect(() => {
    const fetchGuides = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getGuides();
        setGuides(data);
      } catch (err) {
        console.error(err);
        setError("Falha ao carregar guias.");
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, []);

  useEffect(() => {
    if (selectedGuide) {
      setIsContentLoading(true);
    } else {
      setIsContentLoading(false);
    }
  }, [selectedGuide, guideContentMode]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white text-emerald-900">
      <header className="max-w-6xl mx-auto px-6 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center font-black text-white shadow-lg">
            CP
          </div>
          <div>
            <p className="text-xs text-gray-500 font-semibold">
              Guias Estratégicos
            </p>
            <h1 className="text-2xl font-black">Biblioteca</h1>
          </div>
        </div>
        <button
          onClick={onBack}
          className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-emerald-50 transition"
        >
          ← Voltar
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10 text-center">
          <p className="text-sm font-bold text-emerald-600 uppercase tracking-[0.2em]">
            Conteúdo Premium
          </p>
          <h2 className="text-4xl font-black mt-2 mb-4">
            Guias para acelerar sua aprovação
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            PDFs curtos, focados no que realmente cai. Vídeo-resumo opcional e
            link direto para checkout ou download.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-16">Carregando...</div>
        ) : error ? (
          <div className="text-center text-red-600 font-semibold">{error}</div>
        ) : guides.length === 0 ? (
          <div className="text-center text-gray-500 py-16">
            Nenhum guia cadastrado ainda.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg-grid-cols-3 gap-6">
            {guides.map((g) => {
              const isPurchased = purchasedGuia(g);
              const defaultMode = g.previewUrl
                ? "preview"
                : g.videoUrl
                  ? "video"
                  : isPurchased && g.ebookUrl
                    ? "pdf"
                    : "preview";
              return (
                <div
                  key={g.id}
                  className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition hover:-translate-y-1 flex flex-col"
                >
                  <div
                    className="h-44 bg-gray-100 relative cursor-pointer"
                    onClick={() => {
                      setSelectedGuide(g);
                      setGuideContentMode(defaultMode as any);
                    }}
                  >
                    {g.thumb ? (
                      <img
                        src={g.thumb}
                        alt={g.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        Sem thumb
                      </div>
                    )}
                    {g.tag && (
                      <span className="absolute top-3 left-3 bg-white/90 text-emerald-700 text-xs font-black px-3 py-1 rounded-full border border-emerald-100 shadow-sm">
                        {g.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col gap-3">
                    <div>
                      <p className="text-xs font-bold text-emerald-500 mb-1">
                        Guia Estratégico
                      </p>
                      <h3 className="text-xl font-black text-gray-900 leading-tight">
                        {g.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 flex-1">
                      Vídeo: {g.videoUrl ? "incluído" : "opcional"} •
                      PDF/Checkout pronto
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-black text-emerald-700">
                        {g.price || "R$ 0,00"}
                      </span>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {g.videoUrl && (
                          <button
                            onClick={() => {
                              setSelectedGuide(g);
                              setGuideContentMode("video");
                            }}
                            className="bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-2 rounded-xl hover:bg-emerald-50 transition"
                          >
                            Assistir vídeo
                          </button>
                        )}
                        {(g.previewUrl || g.ebookUrl) && (
                          <button
                            onClick={() => {
                              setSelectedGuide(g);
                              setGuideContentMode("preview");
                            }}
                            className="bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-2 rounded-xl hover:bg-emerald-50 transition"
                          >
                            Ver amostra
                          </button>
                        )}
                        {g.checkoutUrl && !isPurchased && (
                          <button
                            onClick={() => {
                              if (!isLoggedIn) {
                                onLogin?.();
                                setSelectedGuide(g);
                                setGuideContentMode(defaultMode as any);
                                return;
                              }
                              window.open(g.checkoutUrl!, "_blank");
                            }}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 rounded-xl shadow-sm"
                          >
                            Comprar guia
                          </button>
                        )}
                        {g.ebookUrl && isPurchased && (
                          <button
                            onClick={() =>
                              window.open(resolveUrl(g.ebookUrl), "_blank")
                            }
                            className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 rounded-xl shadow-sm"
                          >
                            Baixar PDF
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {selectedGuide && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-lg font-black text-gray-900">
                {selectedGuide.title}
              </h3>
              <button
                onClick={() => setSelectedGuide(null)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="relative pb-[56.25%] bg-gray-100">
              {isContentLoading && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40">
                  Carregando...
                </div>
              )}
              {(() => {
                const isPurchased = purchasedGuia(selectedGuide);
                const commonProps = {
                  className: "absolute inset-0 w-full h-full",
                  onLoad: () => setIsContentLoading(false),
                } as const;

                if (guideContentMode === "video" && selectedGuide.videoUrl) {
                  return (
                    <iframe
                      src={selectedGuide.videoUrl}
                      title={selectedGuide.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      {...commonProps}
                    />
                  );
                }

                if (
                  guideContentMode === "preview" &&
                  selectedGuide.previewUrl
                ) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.previewUrl)}
                      title={`${selectedGuide.title} - Prévia`}
                      {...commonProps}
                    />
                  );
                }

                if (
                  guideContentMode === "pdf" &&
                  selectedGuide.ebookUrl &&
                  isPurchased
                ) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.ebookUrl)}
                      title={selectedGuide.title}
                      {...commonProps}
                    />
                  );
                }

                if (selectedGuide.previewUrl) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.previewUrl)}
                      title={`${selectedGuide.title} - Prévia`}
                      {...commonProps}
                    />
                  );
                }
                if (selectedGuide.ebookUrl && isPurchased) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.ebookUrl)}
                      title={selectedGuide.title}
                      {...commonProps}
                    />
                  );
                }

                setIsContentLoading(false);
                return (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm px-4 text-center">
                    Amostra indisponível. Use "Comprar guia" para desbloquear o
                    PDF.
                  </div>
                );
              })()}
            </div>
            <div className="p-4 flex flex-col sm:flex-row gap-3">
              {selectedGuide.videoUrl && (
                <button
                  onClick={() => setGuideContentMode("video")}
                  className="flex-1 bg-white border border-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50"
                >
                  Assistir vídeo
                </button>
              )}
              {(selectedGuide.previewUrl || selectedGuide.ebookUrl) && (
                <button
                  onClick={() => setGuideContentMode("preview")}
                  className="flex-1 bg-white border border-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50"
                >
                  Ver amostra
                </button>
              )}
              {selectedGuide.checkoutUrl && !purchasedGuia(selectedGuide) && (
                <button
                  onClick={() => {
                    if (!isLoggedIn) {
                      onLogin?.();
                      return;
                    }
                    window.open(selectedGuide.checkoutUrl!, "_blank");
                  }}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl transition"
                >
                  Comprar guia
                </button>
              )}
              {selectedGuide.ebookUrl && purchasedGuia(selectedGuide) && (
                <button
                  onClick={() => {
                    const url = resolveUrl(selectedGuide.ebookUrl);
                    window.open(url, "_blank");
                  }}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl transition"
                >
                  Baixar PDF
                </button>
              )}
              <button
                onClick={() => setSelectedGuide(null)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidesPage;
